/**
 * BMSM Campaign Helper Functions
 */

import { Currency } from '../../constants';
import { formatCurrency } from '../currency';
import type { Tier } from '../../constants/campaign/bmsm-types';
import { generateTierTitle } from '../../constants/campaign/bmsm-config';
import type { CartCouponResponse } from '../../model';

/**
 * Infer if user is on first order based on available data
 * 
 * Logic: If user is authenticated, has free shipping, but cart total is below 
 * free shipping threshold, then likely first order (automatic free shipping)
 */
export function inferIsFirstOrder(
  isAuthenticated: boolean,
  isFreeShipping: boolean,
  cartTotalPrice: number,
  freeShippingThreshold: number,
  coupons: CartCouponResponse[] = []
): boolean {
  // Not authenticated = not first order scenario
  if (!isAuthenticated) {
    return false;
  }
  
  // No free shipping = not first order
  if (!isFreeShipping) {
    return false;
  }
  
  // Reached threshold = earned free shipping, not automatic
  if (cartTotalPrice >= freeShippingThreshold) {
    return false;
  }
  
  // Check for free shipping coupon (excluding BMSM coupons)
  const hasFreeShippingCoupon = coupons.some(coupon => 
    /free.*ship/i.test(coupon.code) && 
    !/bmsm/i.test(coupon.code)
  );
  
  // Has free shipping coupon = not first order automatic benefit
  if (hasFreeShippingCoupon) {
    return false;
  }
  
  // All conditions met: authenticated + free shipping + below threshold + no coupon
  // = likely first order with automatic free shipping
  return true;
}

/**
 * Build milestones from tiers
 */
export function buildMilestonesFromTiers(
  tiers: Tier[],
  cartTotalPrice: number,
  currency: Currency
): Array<{
  tier: Tier;
  title: string;
  achieved: boolean;
  statusText: string;
  anchorPercent: number;
}> {
  const tierCount = tiers.length;
  
  return tiers.map((tier, index) => {
    const achieved = cartTotalPrice >= tier.threshold;
    const statusText = achieved 
      ? 'Available' 
      : formatCurrency(currency, tier.threshold);
    
    // Distribute anchors evenly: 0.2, 0.5, 0.8 for 3 tiers
    const anchorPercent = tierCount === 3 
      ? [0.2, 0.5, 0.8][index]
      : (index + 1) / (tierCount + 1);
    
    return {
      tier,
      title: generateTierTitle(tier),
      achieved,
      statusText,
      anchorPercent,
    };
  });
}

/**
 * Calculate progress percentage
 */
export function calculateProgress(
  tiers: Tier[],
  cartTotalPrice: number,
  isFreeShipping: boolean
): number {
  if (tiers.length === 0) return 0;
  
  // Find max threshold
  const maxThreshold = Math.max(...tiers.map(t => t.threshold));
  
  // If reached max, return 100%
  if (cartTotalPrice >= maxThreshold) {
    return 100;
  }
  
  // Build anchor points
  const points: Array<{ threshold: number; percent: number }> = [
    { threshold: 0, percent: 0 }
  ];
  
  tiers.forEach((tier, index) => {
    const anchorPercent = tiers.length === 3
      ? [20, 50, 80][index]
      : ((index + 1) / (tiers.length + 1)) * 100;
    
    points.push({
      threshold: tier.threshold,
      percent: anchorPercent,
    });
  });
  
  // Linear interpolation between points
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    
    if (cartTotalPrice < curr.threshold) {
      const range = curr.threshold - prev.threshold;
      if (range <= 0) return curr.percent;
      
      const ratio = (cartTotalPrice - prev.threshold) / range;
      const progress = prev.percent + ratio * (curr.percent - prev.percent);
      
      // If free shipping is active, ensure minimum progress
      if (isFreeShipping && tiers[0]?.type === 'shipping') {
        return Math.max(progress, points[1]?.percent || 20);
      }
      
      return progress;
    }
  }
  
  return 100;
}

/**
 * Get bottom message for BMSM banner
 */
export function getBottomMessage(params: {
  isAuthenticated: boolean;
  isLikelyFirstOrder: boolean;
  isFreeShipping: boolean;
  tiers: Tier[];
  cartTotalPrice: number;
  shippingFee: number;
  cartTotalDiscount: number;
  currency: Currency;
}): string {
  const {
    isAuthenticated,
    isLikelyFirstOrder,
    isFreeShipping,
    tiers,
    cartTotalPrice,
    shippingFee,
    cartTotalDiscount,
    currency,
  } = params;
  
  // Get first tier (shipping or first discount)
  const firstTier = tiers[0];
  if (!firstTier) return '';
  
  // Check if reached first tier
  const reachedFirstTier = cartTotalPrice >= firstTier.threshold;
  
  // PRE THRESHOLD 1: Before reaching first tier
  if (!reachedFirstTier) {
    // NON-REGISTERED
    if (!isAuthenticated) {
      return 'Sign up to unlock Free Shipping and up to 15% OFF!';
    }
    
    // REGISTERED - First Order
    if (isLikelyFirstOrder) {
      return 'First order? Free Shipping with no minimum spend!';
    }
    
    // REGISTERED - Returning (needs to reach threshold)
    const amountNeeded = firstTier.threshold - cartTotalPrice;
    if (firstTier.type === 'shipping') {
      return `Spend ${formatCurrency(currency, amountNeeded)} more to get free shipping!`;
    } else {
      return `Spend ${formatCurrency(currency, amountNeeded)} more to get ${firstTier.discountPercent}% off!`;
    }
  }
  
  // POST THRESHOLD 1+: After reaching first tier
  // Find next unachieved tier
  const nextTier = tiers.find(t => cartTotalPrice < t.threshold);
  
  if (nextTier) {
    // Still have more tiers to unlock
    const amountNeeded = nextTier.threshold - cartTotalPrice;
    return `Spend ${formatCurrency(currency, amountNeeded)} more to get ${nextTier.discountPercent}% off!`;
  }
  
  // All tiers achieved - show savings
  const savingAmount = cartTotalPrice > 0
    ? shippingFee + cartTotalDiscount
    : shippingFee;
  
  // REGISTERED - show free shipping message
  if (isAuthenticated) {
    return `Free Shipping & ${formatCurrency(currency, savingAmount)} saved so far! — let's see how far it can go!`;
  }
  
  // NON-REGISTERED - no free shipping message
  return `${formatCurrency(currency, savingAmount)} saved so far! — let's see how far it can go!`;
}

